import { logError, logApiError, logInfo, ApiError } from "@/lib/client/errors";
import { useFiltersStore } from "@/stores/filtersStore";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";
import { useUiStore } from "@/stores/uiStore";
import { trackEvent } from "@/lib/client/analytics";
import { IMPLICIT_EXCLUDED_TYPES } from "@/lib/shared/constants";
import { tokenService } from "@/lib/client/tokenService";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";
import { generateUUID } from "./generateUUID";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

export type PriceCursor = { price: number; id: number };
export type TimeCursor = { timestamp: string; id: number };
export type Cursor = TimeCursor | PriceCursor;

export type LoadItemsResult = {
  items: SearchResultItem[];
  total?: number;
  minPrice?: number;
  maxPrice?: number;
  avgPrice?: number;
  nextCursor?: Cursor | null;
  recentItemCount?: number;
};

export type LoadItemsOptions = {
  query: {
    available?: Filter[];
    brands?: Filter[];
    vendors?: Filter[];
    type?: Filter[];
    regions?: Filter[];
    price?: Filter[];
    currency?: Filter[];
    sort?: Filter[];
    size?: Filter[];
  };
  searchText?: string;
  limit: number;
  signal?: AbortSignal;
  exchangeRates?: { [key: string]: number };
  filterid?: number;
  lastItem?: SearchResultItem;
  offset?: number;
  cursor?: Cursor;
  totalOnly?: boolean;
  countOnly?: boolean;
  resumeFrom?: string;
  newFrom?: string;
  jobId?: string;
};

/** Abort in-flight search requests that get no response — a hung request would
 * otherwise leave the items store's `loading` flag stuck true forever. */
const FETCH_TIMEOUT_MS = 15000;
const COUNT_FETCH_TIMEOUT_MS = 60000;
const AUTH_TOKEN_TIMEOUT_MS = 1500;

/**
 * Combines caller cancellation with our request timeout without relying on
 * AbortSignal.any/timeout, which are unavailable in older Safari and iOS
 * WebViews that the app still supports.
 */
const createFetchSignal = (callerSignal: AbortSignal | undefined, timeoutMs: number) => {
  const controller = new AbortController();
  let abortSource: "caller" | "timeout" | null = null;
  const abortFromCaller = () => {
    if (controller.signal.aborted) return;
    abortSource = "caller";
    controller.abort();
  };
  const abortFromTimeout = () => {
    if (controller.signal.aborted) return;
    abortSource = "timeout";
    controller.abort();
  };

  callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
  const timeoutId = setTimeout(abortFromTimeout, timeoutMs);

  return {
    signal: controller.signal,
    getAbortSource: () => abortSource,
    cleanup: () => {
      clearTimeout(timeoutId);
      callerSignal?.removeEventListener("abort", abortFromCaller);
    },
  };
};

const parseJsonResponse = async (response: Response, endpoint: string) => {
  try {
    return await response.json();
  } catch (error) {
    const detail = error instanceof Error && error.message ? `: ${error.message}` : "";
    throw new ApiError(
      `HTTP ${response.status}: Invalid JSON response${detail}`,
      response.status,
      endpoint
    );
  }
};

/**
 * Loads search results.
 *
 * Returns `null` on any failure (network error, timeout, non-2xx, abort) so
 * callers can distinguish "request failed, retry later" from a genuine empty
 * result — returning `{ items: [] }` on errors made the store mark the feed
 * as permanently ended after a single 429.
 */
const loadItemsRequest = async (options: LoadItemsOptions): Promise<LoadItemsResult | null> => {
  let payload: Search = {} as Search;
  let cleanupFetchSignal = () => {};
  let getFetchAbortSource: () => "caller" | "timeout" | null = () => null;
  const fetchTimeoutMs = options.countOnly ? COUNT_FETCH_TIMEOUT_MS : FETCH_TIMEOUT_MS;
  let endpoint = options.countOnly
    ? "/api/v2/search/count"
    : options.totalOnly
      ? "/api/v2/search/stats"
      : "/api/v2/search";
  try {
    const {
      query,
      searchText,
      limit,
      signal,
      exchangeRates,
      filterid,
      offset = 0,
      cursor,
      totalOnly,
      countOnly,
      jobId,
    } = options;
    if (signal?.aborted) return null;

    const fetchAbort = createFetchSignal(signal, fetchTimeoutMs);
    const fetchSignal = fetchAbort.signal;
    cleanupFetchSignal = fetchAbort.cleanup;
    getFetchAbortSource = fetchAbort.getAbortSource;

    let priceRange: [number, number] | undefined = undefined;
    if (query?.price?.[0]?.value) {
      const selectedCurrency = query.currency?.[0]?.value || "USD";
      const rate = exchangeRates?.[selectedCurrency] || 1;
      const [min, max] = (query.price[0].value as string).split(",");
      const parsedMax = parseFloat(max);
      // Don't apply price filter if max is Infinity (default/no filter)
      if (isFinite(parsedMax)) {
        priceRange = [Math.floor(parseFloat(min) / rate), Math.floor(parsedMax / rate)];
      }
    }

    // Get exclude relists, days back, and kurbo mode from store
    const state = useFiltersStore.getState();
    const excludeRelists = state.excludeRelists;
    const excludeAuctions = state.excludeAuctions;
    const daysBack = state.daysBack;
    const kurboMode = state.kurboMode;

    // Search is public. A stale/temporarily unavailable Clerk browser session
    // must not prevent the unauthenticated search request from reaching the
    // API; authenticated requests still attach the token when available.
    let clerkToken: string | null = null;
    try {
      clerkToken = await Promise.race([
        tokenService.getToken(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), AUTH_TOKEN_TIMEOUT_MS)),
      ]);
    } catch {
      clerkToken = null;
    }
    const selectedSort = query.sort?.[0]?.value || "newest";
    const relevanceOnlySort = selectedSort === "relevance";

    payload = {
      searchText,
      filterId: filterid || null,
      limit,
      offset,
      cursor,
      available: query.available?.map((f) => f.value),
      brands: query.brands?.map((f) => ({
        value: parseInt(f.value as string, 10),
        state: f.state || "include",
      })),
      vendors: query.vendors?.map((f) => ({
        value: parseInt(f.value as string, 10),
        state: f.state || "include",
      })),
      types: [
        ...(query.type?.map((f) => ({
          value: f.value,
          state: f.state || "include",
        })) ?? []),
        ...IMPLICIT_EXCLUDED_TYPES.map((v) => ({ value: v, state: "exclude" as const })),
      ],
      regions: query.regions?.map((f) => ({
        value: f.value,
        state: f.state || "include",
      })),
      // "Most Relevant" is a UI-level sort choice. The backend's field sort
      // remains newest while relevance_only deliberately omits that field from
      // ORDER BY, leaving BM25 rank plus a deterministic ID tie-breaker.
      sort: relevanceOnlySort ? "newest" : selectedSort,
      sortMode: searchText ? (relevanceOnlySort ? "relevance_only" : "field") : "relevance",
      priceRange,
      sizeFilters: query.size?.map((f) => ({
        type: f.value || "clothing",
        size: f.size || f.value,
      })),
      currency: query.currency?.[0]?.value || "USD",
      excludeRelists: excludeRelists || undefined,
      excludeAuctions: excludeAuctions || undefined,
      daysBack: daysBack || undefined,
      // TOKEN_UI_ENABLED is a global kill switch: when it's off, nobody
      // should hit the 24h recency gate, signed in or not. Requiring a
      // clerkToken here (as this used to) left signed-out users gated even
      // with the switch off — token *consumption* accounting for signed-in
      // users is a separate concern and is unaffected.
      kurboMode: !TOKEN_UI_ENABLED || (kurboMode ?? false),
      resumeFrom: options.resumeFrom || undefined,
      newFrom: options.newFrom || undefined,
    } as Search;

    endpoint = countOnly
      ? "/api/v2/search/count"
      : totalOnly
        ? "/api/v2/search/stats"
        : "/api/v2/search";
    const res = await fetch(`${API_URL}${endpoint}`, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Token-Job-Id": jobId ?? generateUUID(),
        ...(clerkToken ? { Authorization: `Bearer ${clerkToken}` } : {}),
      },
      body: JSON.stringify(payload, null, 2),
      signal: fetchSignal,
    });

    if (signal?.aborted) {
      return null;
    }

    // Capture token budget from response headers on every reply (200 or 402)
    if (res.headers?.get?.("X-Tokens-Used") != null) {
      useTokenBudgetStore.getState().actions.updateFromHeaders(res.headers);
    }

    // Handle token exhaustion: auto-disable KURBO mode and open the no-tokens modal
    if (res.status === 402) {
      const body = await res.json().catch(() => ({}));
      if (body.error === "TOKEN_LIMIT_EXCEEDED") {
        useFiltersStore.getState().actions.setKurboMode(false);
        if (TOKEN_UI_ENABLED) useUiStore.getState().actions.openKurboNoTokensNag();
        return { items: [] };
      }
    }

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new ApiError(`HTTP ${res.status}: ${text.slice(0, 200)}`, res.status, endpoint);
    }

    const response = await parseJsonResponse(res, endpoint);

    if (countOnly) {
      return {
        items: [],
        total: response.data.total ?? 0,
      };
    }

    if (totalOnly) {
      return {
        items: [],
        total: response.data.total || 0,
        minPrice: response.data.minPrice || 0,
        maxPrice: response.data.maxPrice || Infinity,
        avgPrice: response.data.avgPrice,
      };
    }

    if (response?.data) {
      const data = response.data as {
        items: SearchResultItem[];
        nextCursor?: Cursor | null;
        total?: number;
        recentItemCount?: number;
      };

      // Track first-page searches (not pagination/scroll loads)
      if (!cursor && !totalOnly) {
        try {
          const resultCount = data.items?.length ?? 0;

          trackEvent("search_performed", "search", {
            search_text: searchText || undefined,
            result_count: resultCount,
            total: data.total,
            sort: payload.sort,
            has_brands: !!payload.brands?.length,
            has_vendors: !!payload.vendors?.length,
            has_types: !!payload.types?.length,
            has_regions: !!payload.regions?.length,
            has_price_range: !!payload.priceRange,
            has_size_filters: !!payload.sizeFilters?.length,
            is_zero_results: resultCount === 0,
            device_type:
              typeof window !== "undefined"
                ? window.innerWidth < 768
                  ? "mobile"
                  : window.innerWidth < 1024
                    ? "tablet"
                    : "desktop"
                : undefined,
          });

          // Dedicated zero-results event with full query context for unmet demand analysis
          if (resultCount === 0) {
            trackEvent("zero_results_query", "search", {
              search_text: searchText || undefined,
              brands: payload.brands?.map((b) => b.value),
              vendors: payload.vendors?.map((v) => v.value),
              types: payload.types?.map((t) => t.value),
              regions: payload.regions?.map((r) => r.value),
              price_range: payload.priceRange,
              size_filters: payload.sizeFilters,
              sort: payload.sort,
            });
          }
        } catch {
          // Analytics must never break search
        }
      }

      return data;
    } else {
      logError(new Error("loadItems: no data"), {
        payload: JSON.stringify(payload || {}, null, 2),
        response: JSON.stringify(response || {}, null, 2),
      });
      return null;
    }
  } catch (error: unknown) {
    const err = error as Error;
    if (getFetchAbortSource() === "timeout") {
      logApiError(
        new ApiError(`Request timed out after ${fetchTimeoutMs}ms`, undefined, endpoint, {
          timeoutMs: fetchTimeoutMs,
        }),
        endpoint,
        {
          payload: JSON.stringify(payload || {}, null, 2),
          options: JSON.stringify(options || {}, null, 2),
          timeoutMs: fetchTimeoutMs,
        }
      );
      return null;
    }

    // Safari/WebKit throws "Load failed" TypeError instead of AbortError
    if (
      err?.name === "AbortError" ||
      (err?.name === "TypeError" && err?.message === "Load failed")
    ) {
      logInfo("Fetch aborted");
      return null;
    }

    logApiError(error, endpoint, {
      payload: JSON.stringify(payload || {}, null, 2),
      options: JSON.stringify(options || {}, null, 2),
    });

    return null;
  } finally {
    cleanupFetchSignal();
  }
};

const inFlightStatsRequests = new Map<string, Promise<LoadItemsResult | null>>();
const inFlightCountRequests = new Map<string, Promise<LoadItemsResult | null>>();

const canonicalize = <T>(values: T[] | undefined): T[] => {
  const unique = new Map((values ?? []).map((value) => [JSON.stringify(value), value]));
  return [...unique.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([, value]) => value);
};

const canonicalizeStatefulFilters = <T>(
  filters: Array<{ value: unknown; state?: string }> | undefined,
  normalizeValue: (value: unknown) => T
) => {
  const included = canonicalize(
    filters
      ?.filter((filter) => filter.state !== "exclude")
      .map((filter) => normalizeValue(filter.value))
  );
  const excluded = canonicalize(
    filters
      ?.filter((filter) => filter.state === "exclude")
      .map((filter) => normalizeValue(filter.value))
  );
  if (included.length) {
    const excludedKeys = new Set(excluded.map((value) => JSON.stringify(value)));
    const effectiveIncluded = included.filter((value) => !excludedKeys.has(JSON.stringify(value)));
    return effectiveIncluded.length ? { include: effectiveIncluded } : { none: true };
  }
  return excluded.length ? { exclude: excluded } : undefined;
};

/**
 * Count identity is based only on fields that can change result eligibility.
 * Pagination and presentation fields (limit, offset, cursor, sort, filter id,
 * and the full exchange-rate object) must never create a new count request.
 */
export const getCountRequestKey = (options: LoadItemsOptions) => {
  const { excludeRelists, excludeAuctions, daysBack, kurboMode } = useFiltersStore.getState();
  const { query } = options;
  const selectedCurrency = String(query.currency?.[0]?.value || "USD");
  const rate = options.exchangeRates?.[selectedCurrency] || 1;
  const rawPrice = query.price?.[0]?.value;
  let priceRange: [number, number] | undefined;

  if (rawPrice) {
    const [min, max] = String(rawPrice).split(",");
    const parsedMax = Number.parseFloat(max);
    if (Number.isFinite(parsedMax)) {
      priceRange = [Math.floor(Number.parseFloat(min) / rate), Math.floor(parsedMax / rate)];
    }
  }

  const types = canonicalizeStatefulFilters(query.type, (value) => String(value));
  const includedTypes = new Set("include" in (types ?? {}) ? types?.include : []);
  const excludedTypes = new Set("exclude" in (types ?? {}) ? types?.exclude : []);
  const sizeFilters =
    types && "none" in types
      ? []
      : canonicalize(
          query.size
            ?.filter((filter) => {
              const type = String(filter.value || "clothing");
              return (
                !excludedTypes.has(type) && (includedTypes.size === 0 || includedTypes.has(type))
              );
            })
            .map((filter) => ({
              type: filter.value || "clothing",
              size: filter.size || filter.value,
            }))
        );

  return JSON.stringify({
    searchText: options.searchText?.trim() ?? "",
    available: canonicalize(query.available?.map((filter) => filter.value)),
    brands: canonicalizeStatefulFilters(query.brands, (value) =>
      Number.parseInt(String(value), 10)
    ),
    vendors: canonicalizeStatefulFilters(query.vendors, (value) =>
      Number.parseInt(String(value), 10)
    ),
    types,
    regions: canonicalizeStatefulFilters(query.regions, (value) => String(value)),
    priceRange,
    sizeFilters,
    excludeRelists: excludeRelists || undefined,
    excludeAuctions: excludeAuctions || undefined,
    daysBack: daysBack || undefined,
    kurboMode: !TOKEN_UI_ENABLED || (kurboMode ?? false),
    resumeFrom: options.resumeFrom || undefined,
    newFrom: options.newFrom || undefined,
  });
};

const getStatsRequestKey = (options: LoadItemsOptions) => {
  const { excludeRelists, excludeAuctions, daysBack, kurboMode } = useFiltersStore.getState();

  return JSON.stringify({
    query: options.query,
    searchText: options.searchText,
    filterid: options.filterid,
    exchangeRates: options.exchangeRates,
    resumeFrom: options.resumeFrom,
    newFrom: options.newFrom,
    excludeRelists,
    excludeAuctions,
    daysBack,
    kurboMode,
  });
};

/**
 * Coalesces identical stats reads while they are in flight. Search setup can be
 * re-entered while providers hydrate (and React Strict Mode deliberately
 * replays effects in development); those callers should share one backend
 * query rather than each sending the same expensive request.
 *
 * The shared fetch is not tied to one caller's AbortSignal. Each caller still
 * ignores the result after it is aborted, while another live caller can use it.
 */
export const loadItems = (options: LoadItemsOptions): Promise<LoadItemsResult | null> => {
  if (!options.totalOnly && !options.countOnly) {
    return loadItemsRequest(options);
  }

  if (options.signal?.aborted) {
    return Promise.resolve(null);
  }

  const requests = options.countOnly ? inFlightCountRequests : inFlightStatsRequests;
  const key = options.countOnly ? getCountRequestKey(options) : getStatsRequestKey(options);
  let request = requests.get(key);

  if (!request) {
    request = loadItemsRequest({ ...options, signal: undefined }).finally(() => {
      requests.delete(key);
    });
    requests.set(key, request);
  }

  return request.then((result) => (options.signal?.aborted ? null : result));
};

/**
 * Updates the scroll position bookmark (resume_from) for a saved filter.
 * Fire-and-forget — failures are silently swallowed since this is non-critical.
 */
export const updateFilterPosition = (filterId: number, resumeFrom: string): void => {
  tokenService
    .getToken()
    .then((clerkToken) => {
      fetch(`${API_URL}/api/v2/user/filters/${filterId}/position`, {
        credentials: "include",
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(clerkToken ? { Authorization: `Bearer ${clerkToken}` } : {}),
        },
        body: JSON.stringify({ resumeFrom }),
      }).catch(() => {});
    })
    .catch(() => {});
};
